import React, { createContext, useContext, useState, useCallback } from 'react';

const MemberContext = createContext(null);

// Mock data for development
// Simulating memberships with various renewal statuses to demonstrate proposal functionality
const MOCK_MEMBERSHIPS = [
  {
    id: 1,
    type: 'Individual',
    number: 'HDL001',
    validUntil: '2026-03-15', // Active, plenty of time
    status: 'active',
    joinedDate: '2023-03-15',
    address: {
      line1: '15 Oak Avenue',
      line2: '',
      city: 'Reading',
      postcode: 'RG1 5NP',
    },
    contact: {
      phone: '0118 123 4567',
      mobile: '07700 900001',
      email: 'robert.wilson@example.com',
    },
  },
  {
    id: 2,
    type: 'Joint',
    number: 'HDL009',
    validUntil: '2025-12-15', // Renewal needed soon (within 60 days)
    status: 'active',
    joinedDate: '2024-01-01',
    address: {
      line1: '67 Beech Avenue',
      line2: '',
      city: 'Nottingham',
      postcode: 'NG1 6DQ',
    },
    contact: {
      phone: '0115 901 2345',
      mobile: '07700 900009',
      email: 'peter.morgan@example.com',
    },
  },
  {
    id: 3,
    type: 'Individual',
    number: 'HDL010',
    validUntil: '2025-12-20', // Renewal needed soon (within 60 days)
    status: 'active',
    joinedDate: '2024-01-05',
    address: {
      line1: '12 Sycamore Street',
      line2: '',
      city: 'Leicester',
      postcode: 'LE1 5WL',
    },
    contact: {
      phone: '0116 012 3456',
      mobile: '07700 900010',
      email: 'laura.bennett@example.com',
    },
  },
  {
    id: 4,
    type: 'Individual',
    number: 'HDL011',
    validUntil: '2024-11-10', // Expired
    status: 'expired',
    joinedDate: '2024-02-10',
    address: {
      line1: '15 Oak Avenue',
      line2: '',
      city: 'Reading',
      postcode: 'RG1 5NP',
    },
    contact: {
      phone: '0118 123 9999',
      mobile: '07700 900011',
      email: 'kevin.hughes@example.com',
    },
  },
];

export function MemberProvider({ children }) {
  const [memberships, setMemberships] = useState(MOCK_MEMBERSHIPS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMemberships = useCallback(async () => {
    try {
      setLoading(true);
      // In production, this would be an API call
      // const response = await axios.get('/api/memberships');
      // setMemberships(response.data);

      // Simulating API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMemberships(MOCK_MEMBERSHIPS);
    } catch (err) {
      setError('Failed to fetch memberships');
      console.error('Error fetching memberships:', err);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since we're using static mock data

  const updateAddress = async (membershipId, newAddress) => {
    try {
      setLoading(true);
      // In production, this would be an API call
      // await axios.put(`/api/memberships/${membershipId}/address`, newAddress);

      // Update local state
      setMemberships(current =>
        current.map(membership =>
          membership.id === membershipId ? { ...membership, address: newAddress } : membership
        )
      );
    } catch (err) {
      setError('Failed to update address');
      console.error('Error updating address:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateContact = async (membershipId, newContact) => {
    try {
      setLoading(true);
      // In production, this would be an API call
      // await axios.put(`/api/memberships/${membershipId}/contact`, newContact);

      // Update local state
      setMemberships(current =>
        current.map(membership =>
          membership.id === membershipId ? { ...membership, contact: newContact } : membership
        )
      );
    } catch (err) {
      setError('Failed to update contact details');
      console.error('Error updating contact details:', err);
    } finally {
      setLoading(false);
    }
  };

  const renewMembership = async membershipId => {
    try {
      setLoading(true);
      // In production, this would be an API call
      // await axios.post(`/api/memberships/${membershipId}/renew`);

      // Update local state - add 1 year to validUntil
      setMemberships(current =>
        current.map(membership =>
          membership.id === membershipId
            ? {
                ...membership,
                validUntil: new Date(
                  new Date(membership.validUntil).setFullYear(
                    new Date(membership.validUntil).getFullYear() + 1
                  )
                )
                  .toISOString()
                  .split('T')[0],
              }
            : membership
        )
      );
    } catch (err) {
      setError('Failed to renew membership');
      console.error('Error renewing membership:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  return (
    <MemberContext.Provider
      value={{
        memberships,
        loading,
        error,
        fetchMemberships,
        updateAddress,
        updateContact,
        renewMembership,
        clearError,
        setError,
      }}
    >
      {children}
    </MemberContext.Provider>
  );
}

export const useMember = () => {
  const context = useContext(MemberContext);
  if (!context) {
    throw new Error('useMember must be used within a MemberProvider');
  }
  return context;
};
